import { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { mockOffers } from '@/mocks/offers';
import { mockPartners } from '@/mocks/partners';
import { mockVerticals } from '@/mocks/verticals';
import { mockGeos } from '@/mocks/geos';
import BaseSelect from '@/components/base/BaseSelect';
import BentoCard from '@/components/BentoCard';
import { SkeletonList } from '@/components/Skeleton';
import { Search, Plus, ArrowUpRight, Tag, MapPin, BarChart3, AlertTriangle } from 'lucide-react';

interface DbOffer {
  id: string;
  partner_id: string;
  vertical_id: string;
  geo_id: string;
  payout_model: string;
  cpl_value: number | null;
  cpa_value: number | null;
  revshare_percent: number | null;
  flat_subscription_amount: number | null;
  min_daily_capacity: number | null;
  max_daily_capacity: number | null;
  priority_score: number;
  status: string;
  active: boolean;
  start_date: string;
  end_date: string | null;
  created_at: string;
  updated_at: string;
}

interface DbPartner {
  id: string;
  display_name: string;
}

export default function OffersPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [expandedOffer, setExpandedOffer] = useState<string | null>(null);

  const [offers, setOffers] = useState<DbOffer[]>([]);
  const [partners, setPartners] = useState<DbPartner[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isMockFallback, setIsMockFallback] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [offersRes, partnersRes] = await Promise.all([
        supabase.from('offers').select('*').order('created_at', { ascending: false }),
        supabase.from('partners').select('id, display_name'),
      ]);

      let hasOfferData = false;
      if (offersRes.error) throw offersRes.error;
      if (offersRes.data && offersRes.data.length > 0) {
        setOffers(offersRes.data as DbOffer[]);
        hasOfferData = true;
      } else {
        // Fallback to mock data mapped to DbOffer shape
        const mapped = mockOffers.filter(o => !o.archived_at).map(o => ({
          id: o.id,
          partner_id: o.partner_id,
          vertical_id: o.vertical_id,
          geo_id: o.geo_id,
          payout_model: o.payout_model,
          cpl_value: (o.payout_config as any)?.rate_local || null,
          cpa_value: null,
          revshare_percent: (o.payout_config as any)?.pct_of_value || null,
          flat_subscription_amount: (o.payout_config as any)?.monthly_fee_eur || null,
          min_daily_capacity: (o.caps as any)?.daily_min || null,
          max_daily_capacity: (o.caps as any)?.monthly || null,
          priority_score: o.priority,
          status: o.status,
          active: o.status === 'active',
          start_date: o.effective_from.slice(0, 10),
          end_date: o.effective_until ? o.effective_until.slice(0, 10) : null,
          created_at: o.created_at,
          updated_at: o.updated_at,
        }));
        setOffers(mapped);
      }

      if (partnersRes.data && partnersRes.data.length > 0) {
        setPartners(partnersRes.data as DbPartner[]);
      } else {
        setPartners(mockPartners.map(p => ({ id: p.id, display_name: p.display_name })));
      }

      setIsMockFallback(!hasOfferData);
    } catch (err: any) {
      setError(err?.message || 'Failed to load offers');
      const mapped = mockOffers.filter(o => !o.archived_at).map(o => ({
        id: o.id,
        partner_id: o.partner_id,
        vertical_id: o.vertical_id,
        geo_id: o.geo_id,
        payout_model: o.payout_model,
        cpl_value: (o.payout_config as any)?.rate_local || null,
        cpa_value: null,
        revshare_percent: (o.payout_config as any)?.pct_of_value || null,
        flat_subscription_amount: (o.payout_config as any)?.monthly_fee_eur || null,
        min_daily_capacity: (o.caps as any)?.daily_min || null,
        max_daily_capacity: (o.caps as any)?.monthly || null,
        priority_score: o.priority,
        status: o.status,
        active: o.status === 'active',
        start_date: o.effective_from.slice(0, 10),
        end_date: o.effective_until ? o.effective_until.slice(0, 10) : null,
        created_at: o.created_at,
        updated_at: o.updated_at,
      }));
      setOffers(mapped);
      setPartners(mockPartners.map(p => ({ id: p.id, display_name: p.display_name })));
      setIsMockFallback(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const getPartnerName = (partnerId: string) => {
    const p = partners.find(pt => pt.id === partnerId);
    return p?.display_name || partnerId;
  };

  const getVerticalName = (vid: string) => {
    const v = mockVerticals.find(vx => vx.id === vid);
    return v?.name || vid;
  };

  const getGeoName = (gid: string) => {
    const g = mockGeos.find(geo => geo.id === gid);
    return g?.name || gid;
  };

  const getRateDisplay = (o: DbOffer) => {
    if (o.cpl_value) return `${o.currency || 'EUR'} ${o.cpl_value}`;
    if (o.cpa_value) return `${o.currency || 'EUR'} ${o.cpa_value}`;
    if (o.revshare_percent) return `${o.revshare_percent}%`;
    if (o.flat_subscription_amount) return `${o.currency || 'EUR'} ${o.flat_subscription_amount}`;
    return '—';
  };

  const filtered = useMemo(() => {
    let result = offers;
    if (search) {
      const q = search.toLowerCase();
      result = result.filter(o =>
        getPartnerName(o.partner_id).toLowerCase().includes(q) ||
        getVerticalName(o.vertical_id).toLowerCase().includes(q) ||
        getGeoName(o.geo_id).toLowerCase().includes(q)
      );
    }
    if (statusFilter !== 'all') {
      result = result.filter(o => o.status === statusFilter);
    }
    return result;
  }, [search, statusFilter, offers, partners]);

  const statusBadge = (status: string) => {
    switch (status) {
      case 'active': return { bg: 'var(--success-bg)', color: 'var(--success-text)', border: 'var(--success-border)' };
      case 'paused': return { bg: 'var(--warning-bg)', color: 'var(--warning-text)', border: 'var(--warning-border)' };
      case 'draft': return { bg: 'var(--bg-surface-raised)', color: 'var(--text-tertiary)', border: 'var(--border-primary)' };
      case 'expired': return { bg: 'var(--danger-bg)', color: 'var(--danger-text)', border: 'var(--danger-border)' };
      default: return { bg: 'var(--bg-surface-raised)', color: 'var(--text-tertiary)', border: 'var(--border-primary)' };
    }
  };

  const toggleOffer = (id: string) => {
    setExpandedOffer(prev => prev === id ? null : id);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold" style={{ color: 'var(--text-primary)' }}>Offers</h1>
          <p className="text-sm mt-0.5" style={{ color: 'var(--text-tertiary)' }}>
            {filtered.length} offers
            {isMockFallback && !loading && (
              <span className="ml-2 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border"
                style={{ color: 'var(--warning-text)', borderColor: 'var(--warning-border)', background: 'var(--warning-bg)' }}
              >Mock</span>
            )}
          </p>
        </div>
        <button
          onClick={() => navigate('/offers/new')}
          className="flex items-center gap-1.5 px-3 py-2 text-sm rounded-xl transition-all whitespace-nowrap"
          style={{ background: 'var(--accent)', color: 'var(--text-inverse)' }}
        >
          <Plus size={16} />
          <span className="hidden sm:inline">New Offer</span>
        </button>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-3 rounded-xl border text-sm"
          style={{ borderColor: 'var(--danger-border)', background: 'var(--danger-bg)', color: 'var(--danger-text)' }}
        >
          <AlertTriangle size={16} />
          <span>{error}</span>
          <button onClick={fetchAll} className="ml-auto text-xs underline">Retry</button>
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1 max-w-md group">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 transition-colors" style={{ color: 'var(--text-muted)' }} />
          <input
            type="text"
            placeholder="Search offers..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2 text-sm rounded-xl outline-none transition-all"
            style={{ background: 'var(--bg-input)', border: '1px solid var(--border-primary)', color: 'var(--text-primary)' }}
          />
        </div>
        <BaseSelect
          value={statusFilter}
          onChange={val => setStatusFilter(val)}
          options={[
            { value: 'all', label: 'All statuses' },
            { value: 'active', label: 'Active' },
            { value: 'paused', label: 'Paused' },
            { value: 'draft', label: 'Draft' },
            { value: 'expired', label: 'Expired' },
          ]}
        />
      </div>

      {loading ? (
        <SkeletonList rows={6} />
      ) : (
        <div className="space-y-3">
          {filtered.map(o => {
            const partnerName = getPartnerName(o.partner_id);
            const verticalName = getVerticalName(o.vertical_id);
            const geoName = getGeoName(o.geo_id);
            const rate = getRateDisplay(o);
            const isExpanded = expandedOffer === o.id;
            const monthlyCap = o.max_daily_capacity;
            const used = 0; // We could fetch payout counts per offer, keeping simple for now

            const detailPanel = (
              <div className="space-y-3 pt-2">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {[
                    { label: 'Model', value: o.payout_model.toUpperCase() },
                    { label: 'Rate', value: rate },
                    { label: 'Cap', value: monthlyCap ? `${used}/${monthlyCap}` : '—' },
                    { label: 'Priority', value: String(o.priority_score) },
                  ].map(item => (
                    <div key={item.label} className="rounded-lg p-2.5 border" style={{ background: 'var(--bg-input)', borderColor: 'var(--border-separator)' }}>
                      <p className="text-[10px] uppercase tracking-wider mb-1" style={{ color: 'var(--text-muted)' }}>{item.label}</p>
                      <p className="text-xs font-medium" style={{ color: 'var(--text-secondary)' }}>{item.value}</p>
                    </div>
                  ))}
                </div>
                <div className="flex items-center gap-2 pt-1">
                  <button
                    onClick={() => navigate(`/offers/${o.id}`)}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-medium rounded-lg border transition-colors"
                    style={{ color: 'var(--accent)', borderColor: 'var(--accent)' }}
                  >
                    <ArrowUpRight size={12} />
                    View Offer
                  </button>
                  <button className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-medium rounded-lg border transition-colors"
                    style={{ color: 'var(--text-tertiary)', borderColor: 'var(--border-primary)', background: 'var(--bg-input)' }}
                  >
                    <BarChart3 size={12} />
                    Analytics
                  </button>
                </div>
              </div>
            );

            return (
              <BentoCard
                key={o.id}
                size="full"
                expandable
                expanded={isExpanded}
                onToggle={() => toggleOffer(o.id)}
                active={isExpanded}
                header={
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>{partnerName}</span>
                        <span className="text-[11px]" style={{ color: 'var(--text-muted)' }}>{verticalName}</span>
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium border" style={statusBadge(o.status)}>{o.status}</span>
                      </div>
                      <div className="flex items-center gap-3 mt-1 flex-wrap">
                        <span className="text-[11px] flex items-center gap-1" style={{ color: 'var(--text-muted)' }}>
                          <MapPin size={10} /> {geoName}
                        </span>
                        <span className="text-[11px]" style={{ color: 'var(--text-muted)' }}>{o.payout_model.toUpperCase()}</span>
                        <span className="text-[11px]" style={{ color: 'var(--text-muted)' }}>Priority {o.priority_score}</span>
                      </div>
                    </div>
                    <div className="shrink-0 text-right">
                      <span className="text-xs font-medium" style={{ color: 'var(--text-secondary)' }}>{rate}</span>
                    </div>
                  </div>
                }
                footer={detailPanel}
              >
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs font-medium px-2 py-0.5 rounded-md border"
                    style={{ background: 'var(--bg-input)', color: 'var(--text-secondary)', borderColor: 'var(--border-primary)' }}
                  >
                    {verticalName}
                  </span>
                  <span className="text-xs" style={{ color: 'var(--text-tertiary)' }}>{geoName}</span>
                  <span className="text-xs" style={{ color: 'var(--text-muted)' }}>{monthlyCap ? `${used}/${monthlyCap} cap` : 'No cap'}</span>
                </div>
              </BentoCard>
            );
          })}

          {filtered.length === 0 && (
            <div className="text-center py-16">
              <p className="text-lg font-semibold" style={{ color: 'var(--text-tertiary)' }}>No offers found</p>
              <p className="text-sm mt-2" style={{ color: 'var(--text-muted)' }}>Try adjusting your search or filter.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}